import { useEffect } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { SELLER_AUTH_REFRESHED_EVENT } from '@shared/utils/axios-instance';
import { MantineProvider, createTheme } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import ProtectedRoute from '@shared/components/ProtectedRoute';
import SellerCabinetPage from '@pages/SellerCabinetPage';
import SellerHelpRedirectPage from '@pages/SellerHelpPage/SellerHelpRedirectPage';
import SellerPhoneLoginPage from '@pages/SellerPhoneLoginPage';
import './App.css';

const DROPDOWN_Z_INDEX = 1100;

const theme = createTheme({
  primaryColor: 'dark',
  defaultRadius: 'var(--input-radius)',
  components: {
    Select: {
      defaultProps: {
        comboboxProps: { zIndex: DROPDOWN_Z_INDEX },
      },
    },
    MultiSelect: {
      defaultProps: {
        comboboxProps: { zIndex: DROPDOWN_Z_INDEX },
      },
    },
    Autocomplete: {
      defaultProps: {
        comboboxProps: { zIndex: DROPDOWN_Z_INDEX },
      },
    },
    Popover: {
      defaultProps: {
        zIndex: DROPDOWN_Z_INDEX,
      },
    },
    Menu: {
      defaultProps: {
        zIndex: DROPDOWN_Z_INDEX,
      },
    },
    DateInput: {
      defaultProps: {
        popoverProps: { zIndex: DROPDOWN_Z_INDEX },
      },
    },
  },
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 60 * 1000,
    },
  },
});

/** Запросы, которые упали до refresh, уже не ждут повтор из интерцептора. */
function RecoverQueriesAfterAuthRefresh() {
  const client = useQueryClient();

  useEffect(() => {
    const recover = () => {
      void client.refetchQueries(
        {
          type: 'active',
          predicate: (query) => query.state.status === 'error',
        },
        { cancelRefetch: false },
      );
    };

    const onRefreshed = () => {
      recover();
      window.setTimeout(recover, 0);
    };

    window.addEventListener(SELLER_AUTH_REFRESHED_EVENT, onRefreshed);
    return () => window.removeEventListener(SELLER_AUTH_REFRESHED_EVENT, onRefreshed);
  }, [client]);

  return null;
}

function App() {
  return (
    <MantineProvider theme={theme}>
      <Notifications
        autoClose={4000}
        zIndex={10000}
        position="bottom-right"
        classNames={{ notification: 'seller-notification-item' }}
      />
      <QueryClientProvider client={queryClient}>
        <RecoverQueriesAfterAuthRefresh />
        <BrowserRouter
          basename={import.meta.env.BASE_URL.replace(/\/$/, '') || undefined}
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <Routes>
            <Route path="/help" element={<SellerHelpRedirectPage />} />
            <Route path="/login" element={<SellerPhoneLoginPage />} />
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <SellerCabinetPage />
                </ProtectedRoute>
              }
            />
          </Routes>
        </BrowserRouter>
      </QueryClientProvider>
    </MantineProvider>
  );
}

export default App;
